import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";

import { getMannequinMetadata } from "../../api/mannequin";
import { DEFAULT_MANNEQUIN_VIEW, type MannequinSurfaceViewState } from "../../pages/MannequinSurfaceGrid";
import {
  USE_MOCK,
  useCampaignStateQuery,
  useCampaignStream,
  useControlMutation,
  useCreateCampaignMutation,
  useSurfaceQuery,
  useTrancheMutation,
} from "../adaptive-sampling/api";
import { PRESETS } from "../adaptive-sampling/configurator/presets";
import { betaReflectionEligibility, validSymmetryPolicies } from "../adaptive-sampling/symmetrySemantics";
import type { AxisSpec, CampaignConfig, CampaignState, MannequinFamily, OutputSpec, SamplePoint, SamplerEngine, SamplerPriorityPolicy, SamplerSymmetryPolicy, TailorExpertName } from "../adaptive-sampling/types";
import { Icons } from "../thread-v4/icons";
import { TailorMannequinBenchmark } from "./TailorMannequinBenchmark";
import { TailorSurfaceBuild, type ExtrapolationMode, type SurfaceErrorStats } from "./ThreadV5TailorSurfaceBuild";
import {
  TAILOR_MANNEQUIN_DEMO_AXES,
  TAILOR_MANNEQUIN_MAX_DATABASE_TARGET,
  TAILOR_MANNEQUIN_MAX_EXACT_BUDGET,
  TAILOR_MANNEQUIN_TRAINING_BUDGET,
  type TailorMannequinDemoInput,
} from "./tailorMannequinDemoContract";

type ThreadV5AdaptiveBuilderProps = {
  selectedOutput: string;
  focusXAxis: string;
  focusYAxis: string;
  onAdoptPoint: (inputs: Record<string, number>) => void;
  onPointsChange?: (points: SamplePoint[]) => void;
  /** Reports the active campaign ids + oracle label up so a shared context can
   * let other surfaces (the explorer) overlay this campaign's points. */
  onCampaignChange?: (campaign: { tailorId?: string; lhsId?: string; aircraftLabel?: string }) => void;
  /** Starts the in-browser Mannequin pair on mount for kiosk/iframe demos. */
  autoStart?: boolean;
  /** Initial active dimensionality for standalone 2-D/3-D/high-D embeds. */
  initialDimensions?: number;
  /** Reviewed input names, ranges, and held values from the database interview. */
  initialInputs?: readonly TailorMannequinDemoInput[];
  /** Reviewed Mannequin oracle seed from the database interview. */
  initialMannequinSeed?: number;
  /** Reviewed Mannequin family from the database interview. */
  initialAircraft?: MannequinFamily;
  /** Initial divergence policy; the repeated global-accuracy view defaults open. */
  initialStopPostDivergence?: boolean;
  /** Initial reflection prior for embedded demos. */
  initialSymmetryMode?: TailorSymmetryMode;
  /** Zero-centred input reflected by the initial symmetry prior. */
  initialSymmetryAxis?: string;
  /** Inputs named by the requested coupled aircraft reflection prior. */
  initialSymmetryAxes?: string[];
  /** User-declared utility blended into Tailor's learned acquisition field. */
  initialPriority?: SamplerPriorityPolicy;
  /** Runs a hands-off presentation loop: pick truth, sample, compare, repeat. */
  autoPlay?: boolean;
  /** Completed samples per lane before autoplay moves to the next aircraft. */
  autoPlayTargetBudget?: number;
  /** Delay between accelerated adaptive tranches. */
  autoPlayIntervalMs?: number;
  /** Rotates through 2-D, 3-D, and 7-D when no dimension was requested. */
  autoPlayDimensionCycle?: boolean;
  /** Runs the paired campaigns to this budget while the builder is used as a hidden demo engine. */
  autoRunToBudget?: number;
  /** Create and advance the matched LHS lane. Tailor-only presentation routes disable it. */
  includeLhs?: boolean;
  /** Space-filling points in the first tranche. Defaults to the legacy four-point builder seed. */
  initialSeedSize?: number;
  /** Run campaign orchestration without mounting the builder's surface panels. */
  headless?: boolean;
};

const OUTPUT_ALIASES: Record<string, string> = {
  C_L: "CL",
  cl: "CL",
  C_D: "CD",
  cd: "CD",
  C_Y: "CY",
  cy: "CY",
  C_m: "Cmy",
  Cm: "Cmy",
  cm: "Cmy",
  cmy: "Cmy",
  C_l: "Cmx",
  Cl: "Cmx",
  C_n: "Cmz",
  Cn: "Cmz",
};

const EXPERT_LABELS: Record<TailorExpertName, string> = {
  value_uncertainty: "Value",
  gradient: "Gradient",
  topology: "Topology",
  downstream: "Downstream",
  coverage: "Coverage",
};
const MOCK_TIMER_MODULE = "/src/features/adaptive-sampling/mock/wsSimulator.ts";
const RESPONSE_LABELS: Record<string, string> = {
  CL: "C_L",
  CD: "C_D",
  CY: "C_Y",
  Cmx: "C_l",
  Cmy: "C_m",
  Cmz: "C_n",
};
const RESPONSE_SUBTITLES: Record<string, string> = {
  CL: "lift",
  CD: "drag",
  CY: "side force",
  Cmx: "roll moment",
  Cmy: "pitch moment",
  Cmz: "yaw moment",
};
// The campaign surface API accepts grids from 16×16 upwards. Keep the compact
// diagnostic maps at that contract floor so all six response queries render.
const MAP_STEPS = 16;
const INITIAL_TRANCHE_SIZE = 4;
const DEFAULT_NEXT_TRANCHE_SIZE = 4;
const MIN_TRANCHE_SIZE = 1;
const MAX_TRANCHE_SIZE = 128;
const DEFAULT_MANNEQUIN_SEED = 7;
const MAX_MANNEQUIN_SEED = 1_000_000;
const AUTOPLAY_AIRCRAFT: MannequinFamily[] = ["cessna_like", "fighter_like", "evtol_like"];
const AUTOPLAY_DIMENSIONS = [2, 3, 7] as const;

function autoplayPick(base: number, cycle: number): { aircraft: MannequinFamily; seed: number } {
  const mixed = Math.abs(Math.trunc(base + cycle * 104_729));
  return {
    aircraft: AUTOPLAY_AIRCRAFT[mixed % AUTOPLAY_AIRCRAFT.length] ?? "cessna_like",
    seed: (mixed * 48_271 + 7_919) % (MAX_MANNEQUIN_SEED + 1),
  };
}
// The full operating space the Mannequin oracle responds to. Tailor samples
// every axis marked `vary`; the user can pin any axis to a constant from the
// Training page. Defaulting all to vary is the whole point — true N-D sampling.
type SampleAxisDef = { name: string; units: string; min: number; max: number; fixed: number };
const SAMPLE_AXES: SampleAxisDef[] = [
  ...TAILOR_MANNEQUIN_DEMO_AXES.map((axis) => ({
    name: axis.id,
    units: axis.unit,
    min: axis.min,
    max: axis.max,
    fixed: axis.held,
  })),
];
type AxisSetting = { vary: boolean; min: number; max: number; fixed: number };
type AxisConfig = Record<string, AxisSetting>;
function axisConfigForDimensions(dimensions = SAMPLE_AXES.length): AxisConfig {
  const count = Math.max(1, Math.min(SAMPLE_AXES.length, Math.trunc(dimensions)));
  return Object.fromEntries(SAMPLE_AXES.map((axis, index) => [
    axis.name,
    { vary: index < count, min: axis.min, max: axis.max, fixed: axis.fixed },
  ]));
}
function axisConfigForInputs(inputs: readonly TailorMannequinDemoInput[] | undefined, fallbackDimensions: number): AxisConfig {
  if (!inputs?.length) return axisConfigForDimensions(fallbackDimensions);
  const reviewed = new Map(inputs.map((input) => [input.name, input]));
  return Object.fromEntries(SAMPLE_AXES.map((axis) => {
    const input = reviewed.get(axis.name);
    const min = input && Number.isFinite(input.min) ? input.min : axis.min;
    const max = input && Number.isFinite(input.max) && input.max > min ? input.max : axis.max;
    const fixed = input && Number.isFinite(input.default) ? Math.max(min, Math.min(max, input.default)) : axis.fixed;
    return [axis.name, { vary: Boolean(input), min, max, fixed }];
  }));
}
function axisConfigToInputs(cfg: AxisConfig): AxisSpec[] {
  return SAMPLE_AXES.map((a) => {
    const s = cfg[a.name] ?? { vary: true, min: a.min, max: a.max, fixed: a.fixed };
    return s.vary
      ? { name: a.name, units: a.units, mode: "range", min: s.min, max: s.max }
      : { name: a.name, units: a.units, mode: "fixed", min: s.min, max: s.max, fixedValue: s.fixed };
  });
}
// The integrated presentation uses the powered lift-and-cruise case as its
// crown example. The picker still exposes the conventional and fighter
// Mannequins, but an unconfigured builder now opens on the eVTOL.
const DEFAULT_AIRCRAFT = "evtol_like";
export type TailorSymmetryMode = "off" | "mirror" | "half-and-mirror";
const SYMMETRY_AXIS_OPTIONS = [
  { value: "beta", label: "Beta" },
] as const;
const SYMMETRY_MODE_OPTIONS: Array<{ value: TailorSymmetryMode; label: string }> = [
  { value: "off", label: "Off" },
  // The coupled aircraft reflection (beta + lateral controls/rates reverse
  // together), not a plain beta mirror; enum values kept for the wire contract.
  { value: "mirror", label: "Reflect every run (coupled)" },
  { value: "half-and-mirror", label: "One side + coupled reflection" },
];
const EXTRAPOLATION_MODE_OPTIONS: Array<{ value: ExtrapolationMode; label: string }> = [
  { value: "extrap", label: "Extrap" },
  { value: "linear", label: "Linear" },
  { value: "clamp", label: "Clamp" },
];
const FALLBACK_AIRCRAFT_OPTIONS: TailorAircraftOption[] = [
  { vehicle: "cessna_like", family: "cessna_like", label: "Cessna 172" },
  { vehicle: "fighter_like", family: "fighter_like", label: "Fighter aircraft" },
  { vehicle: "evtol_like", family: "evtol_like", label: "eVTOL aircraft" },
];

type AdaptiveSamplingDebugWindow = Window & {
  __ADAPTIVE_SAMPLING_DEBUG__?: {
    getCampaign?: (id: string) => CampaignState | undefined;
  };
};
type SurfaceErrorState = Partial<Record<"tailor" | "lhs", SurfaceErrorStats | null>>;
type TailorAircraftOption = {
  vehicle: string;
  family: MannequinFamily;
  label: string;
};

function compactNumber(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "-";
  if (Math.abs(value) >= 1000) return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (Math.abs(value) >= 10) return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return value.toLocaleString(undefined, { maximumSignificantDigits: 4 });
}

function sameSurfaceErrorStats(left: SurfaceErrorStats | null | undefined, right: SurfaceErrorStats | null | undefined): boolean {
  if (!left || !right) return left == null && right == null;
  return left.lane === right.lane
    && left.output === right.output
    && left.count === right.count
    && left.totalAbs === right.totalAbs
    && left.l2 === right.l2
    && left.rmse === right.rmse
    && left.gradientRmse === right.gradientRmse
    && left.gradientXRmse === right.gradientXRmse
    && left.gradientYRmse === right.gradientYRmse
    && left.sobolevH1 === right.sobolevH1
    && left.maxAbs === right.maxAbs
    && left.bias === right.bias;
}

function canonicalOutputName(output: string): string {
  return OUTPUT_ALIASES[output] ?? (output || "CL");
}

function labelFromVehicle(vehicle: string): string {
  return vehicle
    .replace(/^synthetic[-_\s]+/i, "")
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => {
      if (/^x57$/i.test(part)) return "X-57";
      if (/^evtol$/i.test(part)) return "eVTOL";
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(" ");
}

function clampMannequinSeed(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_MANNEQUIN_SEED;
  return Math.max(0, Math.min(MAX_MANNEQUIN_SEED, Math.trunc(value)));
}

function clampTrancheSize(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_NEXT_TRANCHE_SIZE;
  return Math.max(MIN_TRANCHE_SIZE, Math.min(MAX_TRANCHE_SIZE, Math.trunc(value)));
}

/** Keep the dense opening in small blocks, then let a production-scale
 * campaign move in bounded streaming blocks. This reduces 10,000-point runs
 * from thousands of browser round trips while respecting the runtime's
 * per-request 128-point contract. */
export function autoRunTrancheSize(currentBudget: number, targetBudget: number): number {
  const remaining = Math.max(0, Math.trunc(targetBudget) - Math.trunc(currentBudget));
  if (remaining === 0) return 0;
  if (currentBudget < TAILOR_MANNEQUIN_MAX_EXACT_BUDGET) {
    return Math.min(8, TAILOR_MANNEQUIN_MAX_EXACT_BUDGET - currentBudget, remaining);
  }
  return Math.min(MAX_TRANCHE_SIZE, remaining);
}

function buildSymmetryPolicies(mode: TailorSymmetryMode, axisNames: string[]): SamplerSymmetryPolicy[] {
  if (mode === "off") return [];
  return [...new Set(axisNames)].map((axisName) => ({
    enabled: true,
    axisName,
    centre: 0,
    sampleSide: "positive" as const,
    sampleHalf: mode === "half-and-mirror",
    mirrorObservations: mode === "mirror" || mode === "half-and-mirror",
  }));
}

function symmetryPoliciesForConfig(
  mode: TailorSymmetryMode,
  axisNames: string[],
  config: AxisConfig,
): SamplerSymmetryPolicy[] {
  return validSymmetryPolicies(axisConfigToInputs(config), buildSymmetryPolicies(mode, axisNames));
}

function symmetrySummary(mode: TailorSymmetryMode, axisNames: string[]): string {
  if (mode === "off") return "off";
  const axes = axisNames.join("+") || "no axes";
  if (mode === "mirror") return `${axes} mirror`;
  return `${axes} half+mirror`;
}

function buildAircraftOptions(metadata: Awaited<ReturnType<typeof getMannequinMetadata>> | undefined): TailorAircraftOption[] {
  const options: TailorAircraftOption[] = [];
  const seen = new Set<string>();
  const add = (option: TailorAircraftOption) => {
    if (seen.has(option.vehicle)) return;
    seen.add(option.vehicle);
    options.push(option);
  };
  for (const vehicle of metadata?.vehicles ?? []) {
    add({
      vehicle,
      family: vehicle,
      label: metadata?.vehicle_labels[vehicle] ?? labelFromVehicle(vehicle),
    });
  }
  for (const option of FALLBACK_AIRCRAFT_OPTIONS) add(option);
  return options.length ? options : FALLBACK_AIRCRAFT_OPTIONS;
}

function prioritizeOutput(outputs: OutputSpec[], outputName: string): OutputSpec[] {
  if (!outputs.some((output) => output.name === outputName)) {
    return [{ name: outputName, weight: 1 }, ...outputs];
  }
  // Selecting a surface changes only what is inspected. A neutral campaign
  // keeps learning CL, drag, side force, and all three moments together.
  return outputs.map((output) => ({ ...output }));
}

function buildDemoConfig(
  stopPostDivergence: boolean,
  selectedOutput: string,
  focusXAxis: string,
  engine: Extract<SamplerEngine, "tailor-mannequin" | "lhs-mannequin">,
  aircraftFamily: MannequinFamily,
  mannequinSeed: number,
  symmetries: SamplerSymmetryPolicy[],
  axisConfig: AxisConfig,
  initialTrancheSize = INITIAL_TRANCHE_SIZE,
  priority?: SamplerPriorityPolicy,
): CampaignConfig {
  const base = PRESETS.tailor_mannequin_stall.build();
  const outputName = canonicalOutputName(selectedOutput);
  const inputs = axisConfigToInputs(axisConfig);
  const validSymmetries = validSymmetryPolicies(inputs, symmetries);
  const axisNames = new Set(inputs.filter((i) => i.mode === "range").map((input) => input.name));
  const isTailor = engine === "tailor-mannequin";
  // Post-divergence stop only makes sense along a varying axis. Prefer the
  // focus axis, else the first varying axis; disable it if nothing varies.
  const firstVaryingAxis = inputs.find((i) => i.mode === "range")?.name;
  const postDivergenceAxis = axisNames.has(focusXAxis) ? focusXAxis : (firstVaryingAxis ?? "alpha");
  return {
    ...base,
    name: isTailor ? "V5 ADB builder - Tailor Mannequin" : "V5 ADB builder - LHS Mannequin",
    inputs,
    budget: initialTrancheSize,
    batchSize: initialTrancheSize,
    outputs: prioritizeOutput(base.outputs, outputName),
    sampler: {
      ...base.sampler,
      engine,
      mannequinFamily: aircraftFamily,
      mannequinSeed,
      // The presentation aircraft is a geometrically symmetric, balanced-
      // propulsion baseline. The policy below is separately guarded so it is
      // used only for reflection-closed beta/lateral-control domains.
      mannequinPathologies: "none",
      initialDesignSize: initialTrancheSize,
      ...(validSymmetries[0] ? { symmetry: validSymmetries[0], symmetries: validSymmetries } : {}),
      ...(isTailor && priority?.enabled ? { priority } : {}),
      ...(isTailor ? {} : { tailorWeights: undefined }),
      // Frontier-first: hold sampling at/below the inferred nonlinearity
      // frontier for the first 3/8 of the demo's adaptive training budget,
      // then release with the post-break-window utility (see SamplerConfig).
      ...(isTailor && axisNames.has("alpha")
        ? {
            frontierFirst: {
              gatePoints: Math.max(1, Math.ceil(
                (TAILOR_MANNEQUIN_TRAINING_BUDGET - initialTrancheSize) * 3 / 8,
              )),
            },
          }
        : {}),
    },
    stopEarlyIfTargetsMet: false,
    postDivergenceSampling: {
      ...base.postDivergenceSampling,
      // A positive post-stall utility is incompatible with a hard frontier
      // filter. Let the soft priority decide how much of that region to buy.
      enabled: isTailor
        && stopPostDivergence
        && !(priority?.postStall?.enabled && priority.postStall.weight > 0)
        && axisNames.size > 0,
      axisName: postDivergenceAxis,
      outputName,
      minPoints: base.postDivergenceSampling?.minPoints ?? 6,
      dropFraction: base.postDivergenceSampling?.dropFraction ?? 0.12,
      guardBand: base.postDivergenceSampling?.guardBand ?? 1,
    },
  };
}

function statusLabel(point: SamplePoint): string {
  if (point.status === "done") return "Done";
  if (point.status === "running") return "Running";
  if (point.status === "queued") return "Queued";
  return "Failed";
}

function selectedByLabel(point: SamplePoint): string {
  const selectedBy = point.samplerDecision?.selectedBy;
  if (!selectedBy) return "initial";
  if (selectedBy === "initial_design" || selectedBy === "mock_score") return selectedBy.replace("_", " ");
  return EXPERT_LABELS[selectedBy] ?? selectedBy;
}

function pointAxisValue(point: SamplePoint, axis: string): string {
  return compactNumber(point.inputs[axis]);
}

function displayAxisName(name: string): string {
  const axis = TAILOR_MANNEQUIN_DEMO_AXES.find((item) => item.id === name);
  return axis ? `${axis.shortLabel} · ${axis.label}` : name;
}

function axisByName(config: CampaignConfig, name: string): AxisSpec | undefined {
  return config.inputs.find((input) => input.name === name);
}

function axisRange(axis: AxisSpec | undefined, fallback: [number, number]): [number, number] {
  if (!axis) return fallback;
  if (axis.mode === "fixed") {
    const fixed = axis.fixedValue ?? fallback[0];
    const min = axis.min ?? fixed - 1;
    const max = axis.max ?? fixed + 1;
    return max > min ? [min, max] : [fixed - 1, fixed + 1];
  }
  const min = axis.min ?? fallback[0];
  const max = axis.max ?? fallback[1];
  return max > min ? [min, max] : fallback;
}

function axisDefault(axis: AxisSpec): number {
  if (axis.mode === "fixed") return axis.fixedValue ?? ((axis.min ?? 0) + (axis.max ?? 0)) / 2;
  return ((axis.min ?? 0) + (axis.max ?? 1)) / 2;
}

function axisStep(axis: AxisSpec): number {
  const [min, max] = axisRange(axis, [axisDefault(axis) - 1, axisDefault(axis) + 1]);
  if (axis.name === "Mach") return 0.005;
  if (axis.name === "throttle") return 0.01;
  return Math.max((max - min) / 120, 0.01);
}

function clampAxisValue(axis: AxisSpec, value: number): number {
  const [min, max] = axisRange(axis, [axisDefault(axis) - 1, axisDefault(axis) + 1]);
  return Math.max(min, Math.min(max, value));
}

function heldInputDefaults(config: CampaignConfig | undefined, xInput = "alpha", yInput = "beta"): Record<string, number> {
  if (!config) return {};
  return Object.fromEntries(config.inputs
    .filter((axis) => axis.name !== xInput && axis.name !== yInput)
    .map((axis) => [axis.name, axisDefault(axis)]));
}

function outputValue(point: SamplePoint, output: string): number | null {
  const aliases = output === "Cmy" ? ["Cmy", "Cm", "C_m", "cm"] : [output, RESPONSE_LABELS[output], output.toLowerCase()];
  for (const source of [point.truthOutputs, point.outputs]) {
    if (!source) continue;
    for (const alias of aliases) {
      const value = source[alias ?? ""];
      if (typeof value === "number" && Number.isFinite(value)) return value;
    }
  }
  for (const alias of aliases) {
    const value = point.predicted?.[alias ?? ""]?.mean;
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }
  return null;
}

function heatColor(value: number, min: number, max: number): string {
  const span = Math.max(1e-9, max - min);
  const t = Math.max(0, Math.min(1, (value - min) / span));
  const hue = 212 - t * 178;
  const lightness = 26 + Math.abs(t - 0.5) * 18;
  return `hsl(${hue} 68% ${lightness}%)`;
}

function pct(value: number, min: number, max: number): number {
  return Math.max(0, Math.min(100, ((value - min) / Math.max(1e-9, max - min)) * 100));
}

type ResponseMap = {
  output: string; label: string; subtitle: string;
  alphaRange: [number, number]; betaRange: [number, number];
  min: number; max: number;
  cells: Array<{ value: number; color: string }>;
  dots: Array<{ id: string; value: number | null; left: number; top: number; status: SamplePoint["status"]; label: string; kind: "seed" | "adaptive"; pickedBy: string; order: number; isCurrent: boolean }>;
};

function responseMapFromSurface(
  state: CampaignState,
  points: SamplePoint[],
  output: string,
  surface: { x: number[]; y: number[]; truthMean?: number[][] },
): ResponseMap | null {
  if (!surface.truthMean?.length || !surface.x.length || !surface.y.length) return null;
  const alphaAxis = axisByName(state.config, "alpha");
  const betaAxis = axisByName(state.config, "beta");
  const [alphaMin, alphaMax] = axisRange(alphaAxis, [surface.x[0] ?? -5, surface.x[surface.x.length - 1] ?? 24]);
  const [betaMin, betaMax] = axisRange(betaAxis, [surface.y[0] ?? -8, surface.y[surface.y.length - 1] ?? 8]);
  const orderedPoints = [...points].sort((left, right) => left.iter - right.iter || left.id.localeCompare(right.id));
  const currentIter = Math.max(0, ...orderedPoints.map((point) => point.iter));
  let seedOrder = 0;
  let adaptiveOrder = 0;
  const pointMeta = new Map<string, { label: string; kind: "seed" | "adaptive"; pickedBy: string; order: number; isCurrent: boolean }>();
  for (const point of orderedPoints) {
    const selectedBy = point.samplerDecision?.selectedBy;
    const isSeed = !selectedBy || selectedBy === "initial_design";
    const order = isSeed ? ++seedOrder : ++adaptiveOrder;
    pointMeta.set(point.id, {
      label: isSeed ? "S" : String(order), kind: isSeed ? "seed" : "adaptive",
      pickedBy: isSeed ? "Seed coverage" : selectedByLabel(point), order,
      isCurrent: point.iter === currentIter && (point.status === "running" || point.status === "queued"),
    });
  }
  const rawCells = [...surface.truthMean].reverse().flat();
  const rawValues = [...rawCells];
  for (const point of points) {
    const value = outputValue(point, output);
    if (value !== null) rawValues.push(value);
  }
  const min = Math.min(...rawValues);
  const max = Math.max(...rawValues);
  const dots = orderedPoints
    .map((point) => {
      const alpha = point.inputs.alpha;
      const beta = point.inputs.beta;
      const meta = pointMeta.get(point.id);
      if (!meta || typeof alpha !== "number" || typeof beta !== "number" || !Number.isFinite(alpha) || !Number.isFinite(beta)) return null;
      return {
        ...meta,
        id: point.id,
        value: outputValue(point, output),
        left: pct(alpha, alphaMin, alphaMax),
        top: 100 - pct(beta, betaMin, betaMax),
        status: point.status,
      };
    })
    .filter((dot): dot is ResponseMap["dots"][number] => dot !== null);
  return {
    output,
    label: RESPONSE_LABELS[output] ?? output,
    subtitle: RESPONSE_SUBTITLES[output] ?? "response",
    alphaRange: [alphaMin, alphaMax],
    betaRange: [betaMin, betaMax],
    min,
    max,
    cells: rawCells.map((value) => ({ value, color: heatColor(value, min, max) })),
    dots,
  };
}

function readMockCampaignState(id: string | undefined): CampaignState | undefined {
  if (!id || !USE_MOCK || typeof window === "undefined") return undefined;
  return (window as AdaptiveSamplingDebugWindow).__ADAPTIVE_SAMPLING_DEBUG__?.getCampaign?.(id);
}

/**
 * Keep a mutation's returned success ahead of a temporarily stale query
 * publication, but deliberately roll back an impossible reserved-budget gap.
 */
export function reconcileAutoRunConfirmedBudget(
  previousConfirmed: number,
  observedCompleted: number,
  hasTornReservedGap: boolean,
): number {
  return hasTornReservedGap
    ? observedCompleted
    : Math.max(previousConfirmed, observedCompleted);
}

function MannequinResponseMaps({ state, points, selectedOutput, heldInputs }: { state: CampaignState | undefined; points: SamplePoint[]; selectedOutput: string; heldInputs: Record<string, number> }) {
  const params = { xInput: "alpha", yInput: "beta", slice: heldInputs, resolution: MAP_STEPS };
  const cl = useSurfaceQuery(state?.id, { ...params, output: "CL" });
  const cd = useSurfaceQuery(state?.id, { ...params, output: "CD" });
  const cy = useSurfaceQuery(state?.id, { ...params, output: "CY" });
  const clMoment = useSurfaceQuery(state?.id, { ...params, output: "Cmx" });
  const cm = useSurfaceQuery(state?.id, { ...params, output: "Cmy" });
  const cn = useSurfaceQuery(state?.id, { ...params, output: "Cmz" });
  const maps = useMemo(() => {
    if (!state) return [];
    return ([
      ["CL", cl.data],
      ["CD", cd.data],
      ["CY", cy.data],
      ["Cmx", clMoment.data],
      ["Cmy", cm.data],
      ["Cmz", cn.data],
    ] as const).flatMap(([output, surface]) => {
      const map = surface ? responseMapFromSurface(state, points, output, surface) : null;
      return map ? [map] : [];
    });
  }, [cd.data, cl.data, clMoment.data, cm.data, cn.data, cy.data, points, state]);
  if (!state || maps.length === 0) return null;
  const completed = points.filter((point) => point.status === "done").length;
  return (
    <section className="v5-mannequin-sweep" aria-label="Mannequin alpha beta force surfaces">
      <div className="v5-mannequin-sweep-head">
        <div>
          <span className="v5-eyebrow">Mannequin alpha/beta sweep</span>
          <strong>Force and moment response surfaces</strong>
        </div>
        <span><em>{completed}</em> / {state.config.budget} sampled</span>
      </div>
      <div className="v5-tailor-map-legend" data-testid="tailor-selection-legend">
        <span><i className="is-seed" />Seed coverage</span>
        <span><i className="is-adaptive" />Tailor order</span>
        <span><i className="is-current" />Current batch</span>
      </div>
      <div className="v5-mannequin-map-grid">
        {maps.map((map) => (
          <article
            key={map.output}
            className={`v5-mannequin-map ${canonicalOutputName(selectedOutput) === map.output ? "is-selected" : ""}`}
            data-testid="mannequin-response-map"
          >
            <header>
              <div><strong>{map.label}</strong><span>{map.subtitle}</span></div>
              <em>{compactNumber(map.min)}..{compactNumber(map.max)}</em>
            </header>
            <div className="v5-mannequin-plot" role="img" aria-label={`${map.label} over alpha and beta`}>
              <div className="v5-mannequin-cells">
                {map.cells.map((cell, index) => (
                  <i
                    key={`${map.output}-${index}`}
                    style={{ "--cell-color": cell.color } as CSSProperties}
                    title={`${map.label} ${compactNumber(cell.value)}`}
                  />
                ))}
              </div>
              {map.dots.map((dot) => (
                <span
                  key={`${map.output}-${dot.id}`}
                  className={`v5-tailor-dot is-${dot.kind} is-${dot.status}${dot.isCurrent ? " is-current" : ""}`}
                  data-testid="tailor-sample-dot" style={{ left: `${dot.left}%`, top: `${dot.top}%` }}
                  title={`${dot.label} ${dot.id}: ${dot.pickedBy}, ${dot.value === null ? "pending" : compactNumber(dot.value)}`}
                >{dot.label}</span>
              ))}
            </div>
            <footer><span>beta {compactNumber(map.betaRange[0])}..{compactNumber(map.betaRange[1])}</span><span>alpha {compactNumber(map.alphaRange[0])}..{compactNumber(map.alphaRange[1])}</span></footer>
          </article>
        ))}
      </div>
    </section>
  );
}

function OracleErrorSummary({ errors }: { errors: SurfaceErrorState }) {
  const rows = [errors.tailor, errors.lhs].filter((error): error is SurfaceErrorStats => Boolean(error));
  if (!rows.length) return null;
  return (
    <div className="v5-adb-error-summary" aria-label="Oracle total error" data-testid="oracle-error-summary">
      <span className="v5-eyebrow">Oracle total error</span>
      {rows.map((error) => (
        <div key={error.lane} className={`v5-adb-error-card is-${error.lane}`}>
          <strong>{error.methodLabel}</strong>
          <span><em>Σ|e|</em><b>{compactNumber(error.totalAbs)}</b></span>
          <span><em>L2</em><b>{compactNumber(error.l2)}</b></span>
          <span><em>RMSE</em><b>{compactNumber(error.rmse)}</b></span>
          <span><em>∇RMSE</em><b>{compactNumber(error.gradientRmse)}</b></span>
          <span><em>Sobolev H1</em><b>{compactNumber(error.sobolevH1)}</b></span>
          <span><em>max |e|</em><b>{compactNumber(error.maxAbs)}</b></span>
          <span><em>bias</em><b>{compactNumber(error.bias)}</b></span>
        </div>
      ))}
    </div>
  );
}

function HeldInputSliders({
  state,
  values,
  onChange,
  onReset,
}: {
  state: CampaignState | undefined;
  values: Record<string, number>;
  onChange: (name: string, value: number) => void;
  onReset: () => void;
}) {
  const axes = useMemo(() => state?.config.inputs.filter((axis) => axis.name !== "alpha" && axis.name !== "beta") ?? [], [state]);
  if (!state || axes.length === 0) return null;
  return (
    <section className="v5-held-inputs" aria-label="Held Mannequin constants" data-testid="held-input-sliders">
      <div className="v5-held-inputs-head">
        <div>
          <span className="v5-eyebrow">Held constants</span>
          <strong>Mannequin slice controls</strong>
        </div>
        <button className="v5-text-button" type="button" onClick={onReset}>
          <Icons.Refresh size={12} />Reset
        </button>
      </div>
      <div className="v5-held-input-grid">
        {axes.map((axis) => {
          const [min, max] = axisRange(axis, [axisDefault(axis) - 1, axisDefault(axis) + 1]);
          const value = clampAxisValue(axis, values[axis.name] ?? axisDefault(axis));
          return (
            <label key={axis.name} className="v5-held-input-slider">
              <span>
                <em>{displayAxisName(axis.name)}</em>
                <strong>{compactNumber(value)}{axis.units ? ` ${axis.units}` : ""}</strong>
              </span>
              <input
                aria-label={displayAxisName(axis.name)}
                min={min}
                max={max}
                step={axisStep(axis)}
                type="range"
                value={value}
                onChange={(event) => onChange(axis.name, Number(event.target.value))}
              />
            </label>
          );
        })}
      </div>
    </section>
  );
}

function orderedSequence(points: SamplePoint[]): SamplePoint[] {
  return [...points].sort((left, right) => left.iter - right.iter || left.id.localeCompare(right.id));
}

const GUIDED_STORY_STEPS = [
  {
    label: "Truth",
    title: "Start with a hidden aircraft truth",
    body: "Mannequin generates a deterministic aircraft ADB from the displayed family and seed. The samplers can buy values from it, but cannot inspect the full surface.",
    next: "Next: seed both samplers",
  },
  {
    label: "Seed",
    title: "Give both methods the same starting budget",
    body: "Tailor and Latin hypercube sampling begin with the same dimension-aware space-filling design inside exactly the same bounds. This keeps the comparison controlled.",
    next: "Next: let Tailor adapt",
  },
  {
    label: "Adapt",
    title: "Watch Tailor choose what to buy next",
    body: "The residual GP is refit after every accelerated tranche. Five explicit experts score uncertainty, gradient, topology, downstream value, and coverage; LHS continues independently.",
    next: "Next: compare surfaces",
  },
  {
    label: "Compare",
    title: "Compare both learned ADBs with truth",
    body: "Each 3-D plot overlays the learned response, low-fidelity prior, and Mannequin oracle. The error strip scores the full visible slice, not only the purchased points.",
    next: "Next: test robustness",
  },
  {
    label: "Repeat",
    title: "Repeat on a new aircraft and a harder dimension",
    body: "A useful claim must survive re-seeding and higher dimension. The next run changes the aircraft truth and cycles from 2-D to 3-D to 7-D.",
    next: "Next aircraft",
  },
] as const;

function AutoplaySequenceLane({ label, points, focusXAxis, focusYAxis }: {
  label: "Tailor" | "LHS";
  points: SamplePoint[];
  focusXAxis: string;
  focusYAxis: string;
}) {
  const ordered = orderedSequence(points);
  const visible = ordered.slice(-14);
  const hidden = Math.max(0, ordered.length - visible.length);
  return (
    <div className={`v5-autoplay-lane is-${label.toLowerCase()}`} role="list" aria-label={`${label} sample sequence`}>
      <strong>{label}</strong>
      {hidden ? <span className="v5-autoplay-more">+{hidden}</span> : null}
      {visible.map((point, index) => {
        const sequence = hidden + index + 1;
        const decision = selectedByLabel(point);
        return (
          <span
            aria-label={`${label} sample ${sequence}: ${displayAxisName(focusXAxis)} ${pointAxisValue(point, focusXAxis)}, ${displayAxisName(focusYAxis)} ${pointAxisValue(point, focusYAxis)}, ${decision}`}
            className={index === visible.length - 1 ? "is-current" : ""}
            key={point.id}
            role="listitem"
          >
            <em>{sequence}</em>
            <i>{pointAxisValue(point, focusXAxis)}</i>
            <i>{pointAxisValue(point, focusYAxis)}</i>
          </span>
        );
      })}
    </div>
  );
}

function AutoplayStory({
  step,
  cycle,
  aircraftLabel,
  seed,
  dimensions,
  targetBudget,
  tailorPoints,
  lhsPoints,
  focusXAxis,
  focusYAxis,
  liveStatus,
  reason,
  ready,
  onNext,
}: {
  step: number;
  cycle: number;
  aircraftLabel: string;
  seed: number;
  dimensions: number;
  targetBudget: number;
  tailorPoints: SamplePoint[];
  lhsPoints: SamplePoint[];
  focusXAxis: string;
  focusYAxis: string;
  liveStatus: string;
  reason: string;
  ready: boolean;
  onNext: () => void;
}) {
  const complete = Math.min(targetBudget, tailorPoints.filter((point) => point.status === "done").length);
  const current = GUIDED_STORY_STEPS[step] ?? GUIDED_STORY_STEPS[0];
  const currentBody = step === 1
    ? `Tailor and Latin hypercube sampling begin with the same ${targetBudget}-point space-filling design inside exactly the same bounds. This keeps the comparison controlled.`
    : current.body;
  const showSequences = step === 1 || step === 2;
  return (
    <section className="v5-autoplay-story" aria-label="Self-playing Tailor story">
      <ol className="v5-story-steps" aria-label="Tailor demonstration parts">
        {GUIDED_STORY_STEPS.map((item, index) => (
          <li className={index === step ? "is-current" : index < step ? "is-complete" : ""} key={item.label} aria-current={index === step ? "step" : undefined}>
            <span>{index + 1}</span><strong>{item.label}</strong>
          </li>
        ))}
      </ol>
      <header>
        <div>
          <span className="v5-eyebrow">Part {step + 1} of {GUIDED_STORY_STEPS.length} · aircraft {cycle + 1}</span>
          <strong>{current.title}</strong>
          <p>{currentBody}</p>
        </div>
        <div className="v5-autoplay-actions">
          <button className="v5-primary-button" type="button" onClick={onNext} disabled={!ready}>{ready ? current.next : liveStatus}</button>
        </div>
      </header>
      <div className="v5-autoplay-facts">
        <span><em>Mannequin truth</em><strong>{aircraftLabel}</strong></span>
        <span><em>Truth seed</em><strong>{seed}</strong></span>
        <span><em>Problem</em><strong>{dimensions}-D · {displayAxisName(focusXAxis)} / {displayAxisName(focusYAxis)} slice</strong></span>
        <span><em>Sampler model</em><strong>6 responses → 5 experts → ARD Matérn GP</strong></span>
      </div>
      <div className="v5-autoplay-progress" aria-label={`${complete} of ${targetBudget} Tailor samples complete`}>
        <i style={{ width: `${targetBudget > 0 ? (complete / targetBudget) * 100 : 0}%` }} />
      </div>
      <div className="v5-story-live" aria-live="polite"><em>{ready ? "Ready" : "Running"}</em><span>{reason}</span></div>
      {showSequences ? (
        <div className="v5-autoplay-sequences">
          <AutoplaySequenceLane label="Tailor" points={tailorPoints} focusXAxis={focusXAxis} focusYAxis={focusYAxis} />
          <AutoplaySequenceLane label="LHS" points={lhsPoints} focusXAxis={focusXAxis} focusYAxis={focusYAxis} />
        </div>
      ) : null}
    </section>
  );
}

export function ThreadV5AdaptiveBuilder({
  selectedOutput,
  focusXAxis,
  focusYAxis,
  onAdoptPoint,
  onPointsChange,
  onCampaignChange,
  autoStart = false,
  initialDimensions = SAMPLE_AXES.length,
  initialInputs,
  initialMannequinSeed = DEFAULT_MANNEQUIN_SEED,
  initialAircraft = DEFAULT_AIRCRAFT,
  initialStopPostDivergence = true,
  initialSymmetryMode = "off",
  initialSymmetryAxis = "beta",
  initialSymmetryAxes,
  initialPriority,
  autoPlay = false,
  autoPlayTargetBudget = 24,
  autoPlayIntervalMs = 850,
  autoPlayDimensionCycle = false,
  autoRunToBudget,
  includeLhs = true,
  initialSeedSize,
  headless = false,
}: ThreadV5AdaptiveBuilderProps) {
  const reviewedDimensionCount = initialInputs?.length ?? initialDimensions;
  const configuredInitialSeedSize = clampTrancheSize(
    initialSeedSize ?? Math.max(16, Math.min(SAMPLE_AXES.length, Math.trunc(reviewedDimensionCount)) * 8),
  );
  const autoRunRequestedBudgetRef = useRef(configuredInitialSeedSize);
  const [autoRunRetryAttempt, setAutoRunRetryAttempt] = useState(0);
  const autoplayBase = useRef(Math.floor(Date.now() / 1_000) % (MAX_MANNEQUIN_SEED + 1)).current;
  const initialAutoplayPick = useRef(autoplayPick(autoplayBase, 0)).current;
  const [selectedAircraft, setSelectedAircraft] = useState<MannequinFamily>(autoPlay ? initialAutoplayPick.aircraft : initialAircraft);
  const [selectedSeed, setSelectedSeed] = useState(autoPlay ? initialAutoplayPick.seed : clampMannequinSeed(initialMannequinSeed));
  const [symmetryMode, setSymmetryMode] = useState<TailorSymmetryMode>(initialSymmetryMode);
  const [symmetryAxes, setSymmetryAxes] = useState<string[]>(() => {
    const requested = initialSymmetryAxes?.length ? initialSymmetryAxes : [initialSymmetryAxis];
    return [...new Set(requested)].filter((axis) => SYMMETRY_AXIS_OPTIONS.some((option) => option.value === axis));
  });
  const [extrapolationMode, setExtrapolationMode] = useState<ExtrapolationMode>("extrap");
  const [axisConfig, setAxisConfig] = useState<AxisConfig>(() => axisConfigForInputs(initialInputs, initialDimensions));
  // Mirror axisConfig in a ref kept in sync synchronously, so commitAxes (fired
  // on input blur) always restarts with the latest bounds regardless of whether
  // React has re-rendered between the keystroke and the blur.
  const axisConfigRef = useRef(axisConfig);
  const [stopPostDivergence, setStopPostDivergence] = useState(initialStopPostDivergence);
  const [nextTrancheSize, setNextTrancheSize] = useState(DEFAULT_NEXT_TRANCHE_SIZE);
  const [syncSurfaceViews, setSyncSurfaceViews] = useState(true);
  const [syncedSurfaceView, setSyncedSurfaceView] = useState<MannequinSurfaceViewState>({ ...DEFAULT_MANNEQUIN_VIEW });
  const [surfaceErrors, setSurfaceErrors] = useState<SurfaceErrorState>({});
  const [heldInputs, setHeldInputs] = useState<Record<string, number>>({});
  const [surfaceHeldInputs, setSurfaceHeldInputs] = useState<Record<string, number>>({});
  const [campaignId, setCampaignId] = useState<string>();
  const [lhsCampaignId, setLhsCampaignId] = useState<string>();
  const [diagnosticsOpen, setDiagnosticsOpen] = useState(false);
  const [mockPulse, setMockPulse] = useState(0);
  const [autoplayCycle, setAutoplayCycle] = useState(0);
  const [storyStep, setStoryStep] = useState(0);
  const autoStartedRef = useRef(false);
  const createTailorMutation = useCreateCampaignMutation({
    onSuccess: (response) => setCampaignId(response.id),
  });
  const createLhsMutation = useCreateCampaignMutation({
    onSuccess: (response) => setLhsCampaignId(response.id),
  });
  const stateQuery = useCampaignStateQuery(campaignId, {
    refetchInterval: campaignId ? 1_000 : false,
    staleTime: 400,
  });
  const lhsStateQuery = useCampaignStateQuery(lhsCampaignId, {
    refetchInterval: lhsCampaignId ? 1_000 : false,
    staleTime: 400,
  });
  useCampaignStream(campaignId);
  useCampaignStream(lhsCampaignId);
  const controlMutation = useControlMutation(campaignId ?? "");
  const lhsControlMutation = useControlMutation(lhsCampaignId ?? "");
  const trancheMutation = useTrancheMutation(campaignId ?? "");
  const lhsTrancheMutation = useTrancheMutation(lhsCampaignId ?? "");
  const mannequinMetadataQuery = useQuery({
    queryKey: ["mannequin", "metadata", "tailor-builder"],
    queryFn: getMannequinMetadata,
    retry: false,
    staleTime: 300_000,
  });
  const aircraftOptions = useMemo(() => buildAircraftOptions(mannequinMetadataQuery.data), [mannequinMetadataQuery.data]);
  const selectedAircraftOption = useMemo(
    () => aircraftOptions.find((option) => option.family === selectedAircraft || option.vehicle === selectedAircraft)
      ?? { vehicle: selectedAircraft, family: selectedAircraft, label: labelFromVehicle(selectedAircraft) },
    [aircraftOptions, selectedAircraft],
  );
  const symmetryEligibility = useMemo(
    () => betaReflectionEligibility(axisConfigToInputs(axisConfig)),
    [axisConfig],
  );
  const symmetryPolicies = useMemo(
    () => symmetryPoliciesForConfig(symmetryMode, symmetryAxes, axisConfig),
    [axisConfig, symmetryAxes, symmetryMode],
  );
  const symmetryLabel = useMemo(() => symmetrySummary(symmetryMode, symmetryAxes), [symmetryAxes, symmetryMode]);
  const benchmarkConfig = useMemo(
    () => buildDemoConfig(
      stopPostDivergence,
      selectedOutput,
      focusXAxis,
      "tailor-mannequin",
      selectedAircraft,
      selectedSeed,
      symmetryPolicies,
      axisConfig,
      configuredInitialSeedSize,
      initialPriority,
    ),
    [axisConfig, configuredInitialSeedSize, focusXAxis, initialPriority, selectedAircraft, selectedOutput, selectedSeed, stopPostDivergence, symmetryPolicies],
  );

  useEffect(() => {
    if ((!campaignId && !lhsCampaignId) || !USE_MOCK || import.meta.env.MODE !== "development") return;
    // Vite can reload the mock router and timer modules independently during demos.
    const importRuntimeModule = new Function("path", "return import(path)") as (
      path: string,
    ) => Promise<typeof import("../adaptive-sampling/mock/wsSimulator")>;
    void importRuntimeModule(MOCK_TIMER_MODULE)
      .then((module: typeof import("../adaptive-sampling/mock/wsSimulator")) => module.ensureTickLoopRunning());
  }, [campaignId, lhsCampaignId]);

  useEffect(() => {
    if ((!campaignId && !lhsCampaignId) || !USE_MOCK) return;
    const timer = window.setInterval(() => setMockPulse((pulse) => (pulse + 1) % 10_000), 500);
    return () => window.clearInterval(timer);
  }, [campaignId, lhsCampaignId]);

  useEffect(() => {
    autoRunRequestedBudgetRef.current = configuredInitialSeedSize;
    setAutoRunRetryAttempt(0);
  }, [campaignId, configuredInitialSeedSize]);

  useEffect(() => {
    const timer = window.setTimeout(() => setSurfaceHeldInputs(heldInputs), 120);
    return () => window.clearTimeout(timer);
  }, [heldInputs]);

  const mockState = useMemo(() => readMockCampaignState(campaignId), [campaignId, mockPulse]);
  const mockLhsState = useMemo(() => readMockCampaignState(lhsCampaignId), [lhsCampaignId, mockPulse]);
  const state = campaignId ? (mockState ?? stateQuery.data ?? createTailorMutation.data?.state) : undefined;
  const lhsState = lhsCampaignId ? (mockLhsState ?? lhsStateQuery.data ?? createLhsMutation.data?.state) : undefined;
  const latestPoints = useMemo(() => {
    const points = [...(state?.points ?? [])];
    points.sort((left, right) => {
      if (right.iter !== left.iter) return right.iter - left.iter;
      return right.id.localeCompare(left.id);
    });
    return points;
  }, [mockPulse, state?.points]);
  const latestLhsPoints = useMemo(() => {
    const points = [...(lhsState?.points ?? [])];
    points.sort((left, right) => {
      if (right.iter !== left.iter) return right.iter - left.iter;
      return right.id.localeCompare(left.id);
    });
    return points;
  }, [lhsState?.points, mockPulse]);

  // The development mock is polled so the headless builder can notice genuine
  // campaign progress. We still create a sorted array on every pulse, even
  // when the mutable store points are unchanged. Publishing those identical
  // arrays made every consumer refit/re-hydrate at 2 Hz (most visibly the
  // High-D response matrix). Compare only fields that can change over a
  // point's lifecycle; avoid serialising the much larger acquisition/ARD audit.
  const latestPointsSignature = useMemo(() => latestPoints.map((point) => {
    const values = (record: Record<string, number> | undefined) => Object.entries(record ?? {})
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, value]) => `${key}:${value}`)
      .join(",");
    const predictions = Object.entries(point.predicted ?? {})
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, value]) => `${key}:${value.mean}:${value.sigma}`)
      .join(",");
    return [
      point.id,
      point.iter,
      point.batchId,
      point.status,
      point.source,
      point.runtimeSec ?? "",
      point.costFlexCredits ?? "",
      values(point.outputs),
      values(point.truthOutputs),
      predictions,
    ].join(":");
  }).join("|"), [latestPoints]);
  const lastPointsPublicationRef = useRef<{
    signature: string;
    callback: typeof onPointsChange;
  } | null>(null);

  useEffect(() => {
    const previous = lastPointsPublicationRef.current;
    if (previous?.signature === latestPointsSignature && previous.callback === onPointsChange) return;
    lastPointsPublicationRef.current = { signature: latestPointsSignature, callback: onPointsChange };
    // Snapshot mutable mock-store records only when the semantic version
    // changes. Parent React state then never observes an object mutate behind
    // its back between publications.
    onPointsChange?.(latestPoints.map((point) => ({
      ...point,
      inputs: { ...point.inputs },
      outputs: point.outputs ? { ...point.outputs } : undefined,
      truthOutputs: point.truthOutputs ? { ...point.truthOutputs } : undefined,
      predicted: point.predicted ? Object.fromEntries(Object.entries(point.predicted)
        .map(([key, value]) => [key, { ...value }])) : undefined,
    })));
  }, [latestPoints, latestPointsSignature, onPointsChange]);
  useEffect(() => {
    if (!campaignId && !lhsCampaignId) return;
    onCampaignChange?.({ tailorId: campaignId, lhsId: lhsCampaignId, aircraftLabel: selectedAircraftOption.label });
  }, [campaignId, lhsCampaignId, selectedAircraftOption.label, onCampaignChange]);
  const currentIteration = latestPoints[0]?.iter ?? state?.iteration ?? 0;
  const currentBatch = latestPoints.filter((point) => point.iter === currentIteration).slice(0, 6);
  const focusPoint = currentBatch.find((point) => point.status === "running")
    ?? currentBatch.find((point) => point.status === "queued")
    ?? currentBatch[0]
    ?? latestPoints[0];
  const expertScores = [...(focusPoint?.samplerDecision?.expertScores ?? [])]
    .sort((left, right) => right.score * right.weight - left.score * left.weight)
    .slice(0, 5);
  const latestDecision = latestPoints.map((point) => point.samplerDecision).find(Boolean);
  const statusText = state
    ? (state.status === "running" || lhsState?.status === "running")
      ? (state.pointsInFlight > 0 || (lhsState?.pointsInFlight ?? 0) > 0)
        ? `Sampling tranche ${Math.max(state.iteration, lhsState?.iteration ?? 0)}`
        : `Ready for tranche ${Math.max(state.iteration, lhsState?.iteration ?? 0) + 1}`
      : state.status
    : "Ready";
  const canControl = Boolean(campaignId && state);
  const busyCreating = createTailorMutation.isPending || (includeLhs && createLhsMutation.isPending);
  const pairedCampaignReady = Boolean(campaignId && (!includeLhs || lhsCampaignId));
  const canAddTranche = Boolean(
    campaignId
    && state
    && (!includeLhs || (lhsCampaignId && lhsState))
    && state.status !== "paused"
    && (!includeLhs || lhsState?.status !== "paused")
    && (state.pointsInFlight + (includeLhs ? lhsState?.pointsInFlight ?? 0 : 0)) === 0
    && !trancheMutation.isPending
    && (!includeLhs || !lhsTrancheMutation.isPending),
  );
  const error = createTailorMutation.error
    ?? stateQuery.error
    ?? controlMutation.error
    ?? trancheMutation.error
    ?? (includeLhs ? createLhsMutation.error : null)
    ?? (includeLhs ? lhsStateQuery.error : null)
    ?? (includeLhs ? lhsControlMutation.error : null)
    ?? (includeLhs ? lhsTrancheMutation.error : null);

  const targetBudget = Math.max(state?.config.budget ?? configuredInitialSeedSize, lhsState?.config.budget ?? configuredInitialSeedSize);
  const handleSurfaceErrorStats = useCallback((lane: "tailor" | "lhs", stats: SurfaceErrorStats | null) => {
    setSurfaceErrors((current) => {
      if (sameSurfaceErrorStats(current[lane], stats)) return current;
      return { ...current, [lane]: stats };
    });
  }, []);
  const resetHeldInputs = useCallback(() => {
    const defaults = heldInputDefaults(state?.config, "alpha", "beta");
    setHeldInputs(defaults);
    setSurfaceHeldInputs(defaults);
  }, [state?.config]);
  const updateHeldInput = useCallback((name: string, value: number) => {
    const axis = state?.config.inputs.find((input) => input.name === name);
    setHeldInputs((current) => ({
      ...current,
      [name]: axis ? clampAxisValue(axis, value) : value,
    }));
  }, [state?.config.inputs]);

  const startDemo = (
    aircraftFamily: MannequinFamily = selectedAircraft,
    mannequinSeed = selectedSeed,
    symmetries: SamplerSymmetryPolicy[] = symmetryPolicies,
    axes: AxisConfig = axisConfigRef.current,
  ) => {
    const seed = clampMannequinSeed(mannequinSeed);
    const effectiveSymmetries = validSymmetryPolicies(axisConfigToInputs(axes), symmetries);
    setSelectedSeed(seed);
    const tailorConfig = buildDemoConfig(stopPostDivergence, selectedOutput, focusXAxis, "tailor-mannequin", aircraftFamily, seed, effectiveSymmetries, axes, configuredInitialSeedSize, initialPriority);
    const lhsConfig = buildDemoConfig(stopPostDivergence, selectedOutput, focusXAxis, "lhs-mannequin", aircraftFamily, seed, effectiveSymmetries, axes, configuredInitialSeedSize, initialPriority);
    setCampaignId(undefined);
    setLhsCampaignId(undefined);
    onCampaignChange?.({});
    setSurfaceErrors({});
    setSyncedSurfaceView({ ...DEFAULT_MANNEQUIN_VIEW });
    const defaults = heldInputDefaults(tailorConfig, "alpha", "beta");
    setHeldInputs(defaults);
    setSurfaceHeldInputs(defaults);
    createTailorMutation.mutate(tailorConfig);
    if (includeLhs) createLhsMutation.mutate(lhsConfig);
  };

  useEffect(() => {
    if (!autoStart || autoStartedRef.current) return;
    autoStartedRef.current = true;
    startDemo();
    // This is deliberately mount-only: query-param defaults are captured at
    // mount, while subsequent controls restart campaigns through their own handlers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart]);

  const handleAircraftChange = (value: MannequinFamily) => {
    setSelectedAircraft(value);
    if (campaignId || lhsCampaignId) startDemo(value, selectedSeed, symmetryPolicies);
  };

  const commitSeed = () => {
    const seed = clampMannequinSeed(selectedSeed);
    setSelectedSeed(seed);
    if (campaignId || lhsCampaignId) startDemo(selectedAircraft, seed, symmetryPolicies);
  };

  const handleSymmetryModeChange = (value: TailorSymmetryMode) => {
    const nextMode = value !== "off" && !symmetryEligibility.eligible ? "off" : value;
    setSymmetryMode(nextMode);
    const nextPolicies = symmetryPoliciesForConfig(nextMode, symmetryAxes, axisConfigRef.current);
    if (campaignId || lhsCampaignId) startDemo(selectedAircraft, selectedSeed, nextPolicies);
  };

  const handleSymmetryAxisToggle = (value: string, enabled: boolean) => {
    const nextAxes = enabled
      ? [...new Set([...symmetryAxes, value])]
      : symmetryAxes.length > 1
        ? symmetryAxes.filter((axis) => axis !== value)
        : symmetryAxes;
    setSymmetryAxes(nextAxes);
    const nextPolicies = symmetryPoliciesForConfig(symmetryMode, nextAxes, axisConfigRef.current);
    if (campaignId || lhsCampaignId) startDemo(selectedAircraft, selectedSeed, nextPolicies);
  };

  // All axisConfig writes go through here so the ref + state stay in lockstep.
  const writeAxisConfig = (next: AxisConfig) => {
    axisConfigRef.current = next;
    setAxisConfig(next);
  };
  // Toggling whether an axis is sampled re-runs the campaign immediately;
  // editing a range bound updates state and re-runs on blur (commitAxes).
  const toggleAxisVary = (name: string, vary: boolean) => {
    if (!vary && SAMPLE_AXES.every((axis) => axis.name === name || !axisConfigRef.current[axis.name]?.vary)) return;
    const next: AxisConfig = { ...axisConfigRef.current, [name]: { ...axisConfigRef.current[name]!, vary } };
    writeAxisConfig(next);
    const nextPolicies = symmetryPoliciesForConfig(symmetryMode, symmetryAxes, next);
    if (campaignId || lhsCampaignId) startDemo(selectedAircraft, selectedSeed, nextPolicies, next);
  };
  const setAxisField = (name: string, field: "min" | "max" | "fixed", value: number) => {
    if (!Number.isFinite(value)) return;
    writeAxisConfig({ ...axisConfigRef.current, [name]: { ...axisConfigRef.current[name]!, [field]: value } });
  };
  const commitAxes = () => {
    const nextPolicies = symmetryPoliciesForConfig(symmetryMode, symmetryAxes, axisConfigRef.current);
    if (campaignId || lhsCampaignId) startDemo(selectedAircraft, selectedSeed, nextPolicies, axisConfigRef.current);
  };
  const varyCount = SAMPLE_AXES.filter((a) => axisConfig[a.name]?.vary).length;

  const completedSamplerPoints = (campaignState: CampaignState | undefined): number => campaignState?.points
    .filter((point) => point.source === "sampler" && point.status === "done").length ?? 0;

  const addTranche = async (sizeOverride?: number, absoluteTargetBudget?: number): Promise<boolean> => {
    if (!campaignId || !state || (includeLhs && (!lhsCampaignId || !lhsState))) return false;
    const trancheSize = clampTrancheSize(sizeOverride ?? nextTrancheSize);
    setNextTrancheSize(trancheSize);
    const currentTailorCount = completedSamplerPoints(state);
    const currentLhsCount = includeLhs ? completedSamplerPoints(lhsState) : currentTailorCount;
    const target = absoluteTargetBudget
      ?? Math.max(currentTailorCount, currentLhsCount) + trancheSize;
    const operations: Array<Promise<{ state: CampaignState; pointIds: string[] }>> = [];
    const tailorRemaining = Math.max(0, target - currentTailorCount);
    const lhsRemaining = Math.max(0, target - currentLhsCount);
    if (tailorRemaining > 0) operations.push(trancheMutation.mutateAsync(tailorRemaining));
    if (includeLhs && lhsRemaining > 0) operations.push(lhsTrancheMutation.mutateAsync(lhsRemaining));
    if (operations.length === 0) return true;

    // A paired run can fail on one lane only. allSettled lets the successful
    // lane publish its state; the next pass targets the absolute endpoint and
    // therefore retries only the lagging lane instead of buying it twice.
    const results = await Promise.allSettled(operations);
    const succeeded = results.every((result) => result.status === "fulfilled");
    if (succeeded) {
      const confirmed = results
        .filter((result): result is PromiseFulfilledResult<{ state: CampaignState; pointIds: string[] }> => result.status === "fulfilled")
        .map((result) => completedSamplerPoints(result.value.state));
      if (confirmed.length > 0) autoRunRequestedBudgetRef.current = Math.min(...confirmed);
    }
    return succeeded;
  };

  const advanceAutoplay = () => {
    const nextCycle = autoplayCycle + 1;
    const nextPick = autoplayPick(autoplayBase, nextCycle);
    const nextAxes = autoPlayDimensionCycle
      ? axisConfigForDimensions(AUTOPLAY_DIMENSIONS[nextCycle % AUTOPLAY_DIMENSIONS.length] ?? initialDimensions)
      : axisConfigRef.current;
    setAutoplayCycle(nextCycle);
    setStoryStep(0);
    setSelectedAircraft(nextPick.aircraft);
    setSelectedSeed(nextPick.seed);
    writeAxisConfig(nextAxes);
    startDemo(nextPick.aircraft, nextPick.seed, symmetryPolicies, nextAxes);
  };

  const autoplayBudget = Math.max(configuredInitialSeedSize, Math.min(TAILOR_MANNEQUIN_MAX_EXACT_BUDGET, Math.trunc(autoPlayTargetBudget)));
  const guidedTargetBudget = storyStep < 2 ? configuredInitialSeedSize : autoplayBudget;
  const autoplayComplete = Boolean(
    state
    && (!includeLhs || lhsState)
    && targetBudget >= guidedTargetBudget
    && state.pointsComplete >= guidedTargetBudget
    && (!includeLhs || (lhsState?.pointsComplete ?? 0) >= guidedTargetBudget),
  );

  useEffect(() => {
    if (!autoPlay || storyStep !== 2 || !campaignId || !state || (includeLhs && (!lhsCampaignId || !lhsState))) return;
    if (canAddTranche && targetBudget < guidedTargetBudget) {
      const remaining = guidedTargetBudget - targetBudget;
      const timer = window.setTimeout(() => { void addTranche(Math.min(8, remaining), targetBudget + Math.min(8, remaining)); }, Math.max(120, autoPlayIntervalMs));
      return () => window.clearTimeout(timer);
    }
    return undefined;
    // The campaign snapshots above deliberately drive this small state machine.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPlay, autoPlayIntervalMs, campaignId, canAddTranche, guidedTargetBudget, includeLhs, lhsCampaignId, lhsState?.pointsComplete, state?.pointsComplete, storyStep, targetBudget]);

  useEffect(() => {
    if (autoRunToBudget == null || !campaignId || !state || !canAddTranche || (includeLhs && (!lhsCampaignId || !lhsState))) return;
    const backgroundBudget = Math.max(configuredInitialSeedSize, Math.min(TAILOR_MANNEQUIN_MAX_DATABASE_TARGET, Math.trunc(autoRunToBudget)));
    const tailorCompletedBudget = completedSamplerPoints(state);
    const lhsCompletedBudget = includeLhs ? completedSamplerPoints(lhsState) : tailorCompletedBudget;
    const confirmedBudget = Math.min(tailorCompletedBudget, lhsCompletedBudget);
    // This ref records confirmed returned state, never an intent. Reconcile it
    // monotonically from the live snapshot: mutateAsync can resolve one React
    // render before the campaign query publishes that returned state, and a
    // stale lower snapshot must not buy the same tranche twice. A fresh/HMR
    // mount still starts at the seed, so an older torn
    // `config.budget > points.length` campaign is repaired from its real point
    // count rather than its inflated configured budget.
    const hasTornReservedGap = state.config.budget > tailorCompletedBudget
      || (includeLhs && (lhsState?.config.budget ?? 0) > lhsCompletedBudget);
    autoRunRequestedBudgetRef.current = reconcileAutoRunConfirmedBudget(
      autoRunRequestedBudgetRef.current,
      confirmedBudget,
      hasTornReservedGap,
    );
    const requestedBudget = autoRunRequestedBudgetRef.current;
    if (requestedBudget >= backgroundBudget) return;
    const trancheSize = autoRunTrancheSize(requestedBudget, backgroundBudget);
    const timer = window.setTimeout(
      () => {
        void addTranche(trancheSize, requestedBudget + trancheSize).then((succeeded) => {
          setAutoRunRetryAttempt((attempt) => succeeded ? 0 : attempt + 1);
        });
      },
      Math.max(120, autoPlayIntervalMs) + Math.min(2_000, autoRunRetryAttempt * 250),
    );
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPlayIntervalMs, autoRunRetryAttempt, autoRunToBudget, campaignId, canAddTranche, includeLhs, lhsCampaignId, lhsState?.pointsComplete, state?.pointsComplete, targetBudget]);

  const storyReady = storyStep === 0
    ? Boolean(campaignId && (!includeLhs || lhsCampaignId))
    : storyStep === 1
      ? Boolean(state && state.pointsComplete >= configuredInitialSeedSize && (!includeLhs || (lhsState?.pointsComplete ?? 0) >= configuredInitialSeedSize))
      : storyStep === 2
        ? autoplayComplete
        : true;

  const nextStoryStep = () => {
    if (!storyReady) return;
    if (storyStep >= GUIDED_STORY_STEPS.length - 1) {
      advanceAutoplay();
      return;
    }
    setStoryStep((step) => step + 1);
  };

  const pauseDemo = () => {
    if (campaignId && state?.status === "running") controlMutation.mutate("pause");
    if (lhsCampaignId && lhsState?.status === "running") lhsControlMutation.mutate("pause");
  };

  const resumeDemo = () => {
    if (campaignId && state?.status === "paused") controlMutation.mutate("resume");
    if (lhsCampaignId && lhsState?.status === "paused") lhsControlMutation.mutate("resume");
  };

  const autoplayStatus = !campaignId || (includeLhs && !lhsCampaignId)
    ? "Choosing truth…"
    : (state?.pointsInFlight ?? 0) + (includeLhs ? lhsState?.pointsInFlight ?? 0 : 0) > 0
      ? `Buying tranche ${Math.max(state?.iteration ?? 0, lhsState?.iteration ?? 0)}…`
      : storyStep === 2 && !autoplayComplete
        ? `Selecting ${state?.pointsComplete ?? 0}/${guidedTargetBudget}…`
        : "Preparing…";
  const autoplayReason = latestDecision?.reason
    ?? (includeLhs
      ? "Both methods receive the same bounds and budget; only their point-selection policy differs."
      : "Tailor is fitting the multi-output ADB and updating its next-point decision after every completed solve.");

  if (headless) return null;

  return (
    <section className={`v5-adb-builder${autoPlay ? ` is-story-step-${storyStep}` : ""}`} aria-label="Adaptive ADB builder">
      {autoPlay ? (
        <AutoplayStory
          step={storyStep}
          cycle={autoplayCycle}
          aircraftLabel={selectedAircraftOption.label}
          seed={selectedSeed}
          dimensions={varyCount}
          targetBudget={guidedTargetBudget}
          tailorPoints={latestPoints}
          lhsPoints={latestLhsPoints}
          focusXAxis={focusXAxis}
          focusYAxis={focusYAxis}
          liveStatus={autoplayStatus}
          reason={autoplayReason}
          ready={storyReady}
          onNext={nextStoryStep}
        />
      ) : null}
      <div className="v5-adb-builder-head">
        <div className="v5-adb-builder-title">
          <span className="v5-eyebrow">ADB Builder</span>
          <strong>{includeLhs ? "Tailor vs LHS" : "Tailor ADB generator"}</strong>
          <span>{state?.samplerRuntime?.label ?? selectedAircraftOption.label}</span>
        </div>
        <div className="v5-adb-builder-controls">
          <button className="v5-primary-button" type="button" onClick={() => startDemo()} disabled={busyCreating}>
            <Icons.Sparkle size={13} />{campaignId ? "Fresh run" : "Start demo"}
          </button>
          {pairedCampaignReady ? (
            <>
              <label className="v5-adb-tranche-control">
                <span>Next</span>
                <input
                  aria-label="Next tranche size"
                  min={MIN_TRANCHE_SIZE}
                  max={MAX_TRANCHE_SIZE}
                  step={1}
                  type="number"
                  value={nextTrancheSize}
                  onBlur={() => setNextTrancheSize((value) => clampTrancheSize(value))}
                  onChange={(event) => setNextTrancheSize(clampTrancheSize(Number(event.target.value)))}
                />
              </label>
              <button className="v5-text-button" type="button" onClick={() => addTranche()} disabled={!canAddTranche}>
                <Icons.GridIcon size={12} />Add tranche +{nextTrancheSize}
              </button>
            </>
          ) : null}
          <button
            aria-expanded={diagnosticsOpen}
            className="v5-text-button"
            type="button"
            onClick={() => setDiagnosticsOpen((open) => !open)}
          >
            <Icons.GridIcon size={12} />Diagnostics
          </button>
          {canControl && state?.status === "running" ? (
            <button className="v5-text-button" type="button" onClick={pauseDemo}>
              <Icons.Close size={12} />Pause
            </button>
          ) : null}
          {canControl && state?.status === "paused" ? (
            <button className="v5-text-button" type="button" onClick={resumeDemo}>
              <Icons.Refresh size={12} />Resume
            </button>
          ) : null}
        </div>
      </div>

      <div className="v5-adb-setup">
        <label className="v5-adb-aircraft-control">
          <span>Aircraft</span>
          <select
            aria-label="Tailor aircraft"
            value={selectedAircraftOption.vehicle}
            onChange={(event) => handleAircraftChange(event.target.value)}
            disabled={busyCreating}
          >
            {aircraftOptions.map((option) => (
              <option key={option.vehicle} value={option.vehicle}>{option.label}</option>
            ))}
          </select>
        </label>
        <label className="v5-adb-seed-control">
          <span>Seed</span>
          <input
            aria-label="Tailor seed"
            min={0}
            max={MAX_MANNEQUIN_SEED}
            step={1}
            type="number"
            value={selectedSeed}
            onBlur={commitSeed}
            onChange={(event) => setSelectedSeed(clampMannequinSeed(Number(event.target.value)))}
            onKeyDown={(event) => {
              if (event.key === "Enter") event.currentTarget.blur();
            }}
            disabled={busyCreating}
          />
        </label>
        <label className="v5-adb-symmetry-control">
          <span>Symmetry</span>
          <select
            aria-label="Symmetry mode"
            value={symmetryEligibility.eligible ? symmetryMode : "off"}
            onChange={(event) => handleSymmetryModeChange(event.target.value as TailorSymmetryMode)}
            disabled={busyCreating || !symmetryEligibility.eligible}
          >
            {SYMMETRY_MODE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
        <fieldset className="v5-adb-symmetry-axis-control" disabled={symmetryMode === "off" || busyCreating || !symmetryEligibility.eligible}>
          <legend>Axes</legend>
          <div role="group" aria-label="Symmetry axes">
            {SYMMETRY_AXIS_OPTIONS.map((option) => (
              <label key={option.value}>
                <input
                  type="checkbox"
                  aria-label={`${option.label} symmetry`}
                  checked={symmetryAxes.includes(option.value)}
                  onChange={(event) => handleSymmetryAxisToggle(option.value, event.target.checked)}
                />
                <span>{option.label.replace(" = 0", "")}</span>
              </label>
            ))}
          </div>
        </fieldset>
        <span className={`v5-adb-symmetry-status${symmetryPolicies.length > 0 ? " is-active" : ""}${symmetryEligibility.eligible ? "" : " is-warning"}`} role="status">
          {symmetryEligibility.eligible
            ? symmetryPolicies.length > 0
              ? "Active coupled map: beta, delta-a, delta-r, P and R reverse together · clean symmetric airframe + balanced/common propulsion only"
              : "Symmetry off · available only as the coupled beta/lateral-state map"
            : `${symmetryEligibility.reason}${symmetryMode !== "off" ? " Requested mode is paused and will restore when the envelope is reflection-closed." : ""}`}
        </span>
        <label className="v5-adb-extrapolation-control">
          <span>Extrap</span>
          <select
            aria-label="Extrapolation model"
            value={extrapolationMode}
            onChange={(event) => setExtrapolationMode(event.target.value as ExtrapolationMode)}
          >
            {EXTRAPOLATION_MODE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
        <div className="v5-adb-setup-toggles">
          <label className="v5-adb-switch">
            <input
              checked={stopPostDivergence}
              onChange={(event) => setStopPostDivergence(event.target.checked)}
              type="checkbox"
            />
            <span />
            <em>Stop post-divergence</em>
          </label>
          <label className="v5-adb-switch">
            <input
              checked={syncSurfaceViews}
              onChange={(event) => setSyncSurfaceViews(event.target.checked)}
              type="checkbox"
            />
            <span />
            <em>Sync views</em>
          </label>
        </div>
      </div>

      <section className="v5-adb-axes" aria-label="Sampler dimensions">
        <div className="v5-adb-axes-head">
          <span className="v5-eyebrow">Sample axes</span>
          <strong data-testid="sample-dim-count">{varyCount}-D</strong>
          <span className="v5-adbc-panel-note">Tailor samples every checked axis at once — uncheck to pin one as a constant.</span>
        </div>
        <div className="v5-adb-axis-grid">
          {SAMPLE_AXES.map((axis) => {
            const setting = axisConfig[axis.name]!;
            const busy = busyCreating;
            const disableVaryToggle = busy || (setting.vary && varyCount <= 1);
            return (
              <div key={axis.name} className={`v5-adb-axis ${setting.vary ? "is-vary" : "is-fixed"}`} data-testid={`axis-${axis.name}`}>
                <label className="v5-adb-switch">
                  <input type="checkbox" checked={setting.vary} disabled={disableVaryToggle}
                         onChange={(event) => toggleAxisVary(axis.name, event.target.checked)} />
                  <span />
                  <em>{displayAxisName(axis.name)}{axis.units ? <i> {axis.units}</i> : null}</em>
                </label>
                {setting.vary ? (
                  <span className="v5-adb-axis-range">
                    <input type="number" aria-label={`${displayAxisName(axis.name)} min`} value={setting.min} disabled={busy}
                           onChange={(event) => setAxisField(axis.name, "min", Number(event.target.value))} onBlur={commitAxes} />
                    <i>–</i>
                    <input type="number" aria-label={`${displayAxisName(axis.name)} max`} value={setting.max} disabled={busy}
                           onChange={(event) => setAxisField(axis.name, "max", Number(event.target.value))} onBlur={commitAxes} />
                  </span>
                ) : (
                  <input className="v5-adb-axis-fixed" type="number" aria-label={`${displayAxisName(axis.name)} fixed`} value={setting.fixed} disabled={busy}
                         onChange={(event) => setAxisField(axis.name, "fixed", Number(event.target.value))} onBlur={commitAxes} />
                )}
              </div>
            );
          })}
        </div>
      </section>

      <div className="v5-adb-builder-body">
        <div className="v5-adb-run-strip">
          <span className={`v5-adb-run-state is-${state?.status ?? "ready"}`}>
            <i />{statusText}
          </span>
          <span><em>Aircraft</em><strong>{selectedAircraftOption.label}</strong></span>
          <span><em>Seed</em><strong>{selectedSeed}</strong></span>
          {symmetryPolicies.length > 0 ? <span><em>Symmetry</em><strong>{symmetryLabel}</strong></span> : null}
          <span><em>Extrap</em><strong>{extrapolationMode}</strong></span>
          <span><em>Tailor</em><strong>{state ? `${state.pointsComplete}/${state.config.budget}` : `0/${configuredInitialSeedSize}`}</strong></span>
          {includeLhs ? <span><em>LHS</em><strong>{lhsState ? `${lhsState.pointsComplete}/${lhsState.config.budget}` : `0/${configuredInitialSeedSize}`}</strong></span> : null}
          <span><em>Budget</em><strong>{targetBudget}</strong></span>
          <span><em>Policy</em><strong>{stopPostDivergence ? "gated" : "open"}</strong></span>
          {state?.samplerRuntime?.backendEngine ? (
            <span title={state.samplerRuntime.navigatorSha256
              ? `Historical Navigator artifact provenance: ${state.samplerRuntime.navigatorSha256}`
              : undefined}>
              <em>Runtime</em>
              <strong>{state.samplerRuntime.backendEngine} · configured five-expert policy</strong>
            </span>
          ) : null}
        </div>

        <OracleErrorSummary errors={surfaceErrors} />

        <HeldInputSliders
          state={state}
          values={heldInputs}
          onChange={updateHeldInput}
          onReset={resetHeldInputs}
        />

        {diagnosticsOpen ? (
          <section className="v5-adb-details" data-testid="tailor-demo-details">
            <div className="v5-adb-details-head">
              <span><Icons.GridIcon size={13} />Diagnostics</span>
              <em>force maps, picked runs, acquisition weights</em>
            </div>
            <div className="v5-adb-details-body">
            <div className="v5-adb-run-strip is-detail">
              <span><em>In flight</em><strong>{(state?.pointsInFlight ?? 0) + (includeLhs ? lhsState?.pointsInFlight ?? 0 : 0)}</strong></span>
              <span><em>Mannequin</em><strong>{state?.samplerRuntime?.mannequinInstanceId ?? "new"}</strong></span>
              <span><em>Surrogate</em><strong>{state?.samplerRuntime?.surrogate ?? "waiting"}</strong></span>
              <span><em>Policy source</em><strong>{state?.samplerRuntime?.policySource ?? "probe"}</strong></span>
              <span><em>Frontier</em><strong>{compactNumber(latestDecision?.frontierLimit)}</strong></span>
            </div>

            <MannequinResponseMaps state={state} points={latestPoints} selectedOutput={selectedOutput} heldInputs={surfaceHeldInputs} />

            <div className="v5-adb-builder-grid">
              <div className="v5-adb-run-table" role="table" aria-label="Tailor picked runs">
                <div className="v5-adb-run-row is-head" role="row">
                  <span role="columnheader">Run</span>
                  <span role="columnheader">{focusXAxis ? displayAxisName(focusXAxis) : "X"}</span>
                  <span role="columnheader">{focusYAxis ? displayAxisName(focusYAxis) : "Y"}</span>
                  <span role="columnheader">Picked by</span>
                  <span role="columnheader">Status</span>
                </div>
                {currentBatch.length ? currentBatch.map((point) => (
                  <button key={point.id} className="v5-adb-run-row" role="row" type="button" onClick={() => onAdoptPoint(point.inputs)}>
                    <span role="cell">{point.id}</span>
                    <span role="cell">{pointAxisValue(point, focusXAxis)}</span>
                    <span role="cell">{pointAxisValue(point, focusYAxis)}</span>
                    <span role="cell">{selectedByLabel(point)}</span>
                    <span role="cell"><i className={`is-${point.status}`} />{statusLabel(point)}</span>
                  </button>
                )) : (
                  <div className="v5-adb-run-empty">
                    <Icons.GridIcon size={17} />
                    <span>No adaptive batch yet</span>
                  </div>
                )}
              </div>

              <div className="v5-adb-expert-panel">
                <div>
                  <span className="v5-eyebrow">Tailor experts</span>
                  <strong>{focusPoint?.samplerDecision?.reason ?? "Waiting for first proposal"}</strong>
                </div>
                <div className="v5-adb-expert-bars">
                  {expertScores.length ? expertScores.map((expert) => (
                    <span key={expert.name}>
                      <em>{EXPERT_LABELS[expert.name]}</em>
                      <i style={{ width: `${Math.max(4, Math.min(100, expert.score * expert.weight * 100))}%` }} />
                      <strong>{compactNumber(expert.score * expert.weight)}</strong>
                    </span>
                  )) : (
                    <p>Acquisition weights arrive after the seed batch.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
        ) : null}

        <div className={`v5-tailor-compare-grid${includeLhs ? "" : " is-tailor-only"}`} data-testid="tailor-lhs-compare-grid">
          <TailorSurfaceBuild
            campaignId={campaignId}
            state={state}
            points={latestPoints}
            selectedOutput={selectedOutput}
            focusXAxis={focusXAxis}
            focusYAxis={focusYAxis}
            methodLabel="Tailor"
            sampleLabel="Tailor"
            lane="tailor"
            viewState={syncSurfaceViews ? syncedSurfaceView : undefined}
            onViewStateChange={syncSurfaceViews ? setSyncedSurfaceView : undefined}
            onErrorStatsChange={handleSurfaceErrorStats}
            sliceOverrides={surfaceHeldInputs}
            extrapolationMode={extrapolationMode}
          />
          {includeLhs ? <TailorSurfaceBuild
              campaignId={lhsCampaignId}
              state={lhsState}
              points={latestLhsPoints}
              selectedOutput={selectedOutput}
              focusXAxis={focusXAxis}
              focusYAxis={focusYAxis}
              methodLabel="LHS"
              sampleLabel="LHS"
              lane="lhs"
              viewState={syncSurfaceViews ? syncedSurfaceView : undefined}
              onViewStateChange={syncSurfaceViews ? setSyncedSurfaceView : undefined}
              onErrorStatsChange={handleSurfaceErrorStats}
              sliceOverrides={surfaceHeldInputs}
              extrapolationMode={extrapolationMode}
            /> : null}
        </div>

        {includeLhs ? <TailorMannequinBenchmark
          config={benchmarkConfig}
          output={canonicalOutputName(selectedOutput)}
          truthSeed={selectedSeed}
          aircraftLabel={selectedAircraftOption.label}
        /> : null}

        {error ? <div className="v5-adb-builder-error"><Icons.Info size={13} />{error.message}</div> : null}
      </div>
    </section>
  );
}
